import axios from "axios";
import jwtDecode from "jwt-decode";
import { getLocalStorage, setLocalStorage } from "utils/localStorage";
import {
  baseURL,
  errorHandler,
  httpHandler,
  urls,
  cleanup,
  buildQueryParams,
} from "./constants";

const apiService = (() => {
  const httpInstance = axios.create({
    baseURL,
    headers: { Authorization: getLocalStorage("authToken", "") },
  });

  const buildEndpoints = (url, additionalEndpoints) => ({
    get: (id, queryParams, filters) =>
      httpHandler(() => {
        return httpInstance.get(
          `${url}${id ? `/${id}` : ""}?${buildQueryParams(queryParams)}&${filters !== undefined ? buildQueryParams(filters) : ""}`,
        );
      }),
    create: (formBody) => httpHandler(() => httpInstance.post(url, formBody)),
    update: (id, formBody) =>
      httpHandler(() => httpInstance.put(`${url}/${id}`, formBody)),
    delete: (id, queryParams) =>
      httpHandler(() =>
        httpInstance.delete(`${url}/${id}?${buildQueryParams(queryParams)}`),
      ),
    ...additionalEndpoints,
  });

  const token = {
    set: (token) => {
      httpInstance.defaults.headers.Authorization = `${token}`;
    },
    unset: () => {
      httpInstance.defaults.headers.Authorization = "";
    },
    get: () => httpInstance.defaults.headers.Authorization,
    body: () => jwtDecode(httpInstance.defaults.headers.Authorization),
    isPartTime: () =>
      jwtDecode(httpInstance.defaults.headers.Authorization).employeeType ===
      "PT",
  };

  return {
    auth: {
      refresh: () =>
        httpHandler(() => httpInstance.post(urls.CHECK_TOKEN_ENDPOINT))
          .then((data) => {
            httpInstance.defaults.headers.Authorization = data.authToken;
            setLocalStorage("authToken", data.authToken);
            return data.authToken;
          })
          .catch(() => {
            token.unset();
            cleanup();
            return undefined;
          }),
      login: ({ username, password }) =>
        httpHandler(() =>
          httpInstance.post(urls.BASIC_LOGIN_ENDPOINT, {
            username,
            password,
          }),
        ).then((data) => {
          setLocalStorage("authToken", data.authToken);
          token.set(data.authToken);
        }),
      logout: () => {
        token.unset();
        cleanup();
      },
      isLoggedIn: () => !!token.get(),
      token,
      permissions: () => token.body().role,
    },

    getTransactions: () =>
      httpInstance
        .get(urls.TRANSACTION_ENDPOINT)
        .then((response) => response.data),
    uploadImage: async (file) => {
      const data = new FormData();
      data.append("file", file);
      const response = await httpInstance
        .post(urls.UPLOAD_ENDPOINT, data, {
          headers: {
            accept: "application/json",
            "Accept-Language": "en-US,en;q=0.8",
            "Content-Type": "multipart/form-data",
          },
        })
        .catch(errorHandler);
      return response.data.path;
    },
    getMenuItems: () =>
      httpInstance.get(urls.MENU_ENDPOINT).then((response) => response.data),
    getMenuItem: (id) =>
      httpInstance
        .get(urls.MENU_ENDPOINT + "/" + id)
        .then((response) => response.data),
    createMenuItem: async function* (formBody, file) {
      var imgPath = undefined;
      if (file) imgPath = await this.uploadImage(file);
      yield true;

      yield httpInstance
        .post(urls.MENU_ENDPOINT, { ...formBody, imgPath })
        .catch(errorHandler);
    },
    deleteMenuItem: (id) =>
      httpInstance
        .delete(urls.MENU_ENDPOINT + "/" + id)
        .then((response) => response.data)
        .catch(errorHandler),
    deleteParent: (id) =>
      httpInstance
        .delete(urls.USER_ENDPOINT + "/" + id)
        .then((response) => response.data)
        .catch(errorHandler),
    updateMenuItem: async function* (id, formBody, file) {
      var imgPath = undefined;
      if (file) imgPath = await this.uploadImage(file);
      yield true;
      yield httpInstance
        .put(urls.MENU_ENDPOINT + "/" + id, {
          ...formBody,
          ...(imgPath && { imgPath }),
        })
        .then((response) => response.data)
        .catch(errorHandler);
    },

    getOrders: () =>
      httpInstance.get(urls.ORDER_ENDPOINT).then((response) => response.data),
    getAddPointsRecords: () =>
      httpInstance
        .get(urls.ADD_POINTS_ENDPOINT)
        .then((response) => response.data),
    getAddPointsRecord: (id) =>
      httpInstance
        .get(`${urls.ADD_POINTS_ENDPOINT}/${id}`)
        .then((response) => response.data),
    leave: buildEndpoints(urls.LEAVE_ENDPOINT, {
      self: (params = {}) =>
        httpHandler(() => httpInstance.get(urls.LEAVE_ENDPOINT + "/self", { params })),
      pending: () =>
        httpHandler(() => httpInstance.get(urls.LEAVE_ENDPOINT + "/pending")),
      approve: (id, data) =>
        httpHandler(() =>
          httpInstance.put(urls.LEAVE_ENDPOINT + `/${id}/approve`, data),
        ),
      approveAll: () =>
        httpHandler(() =>
          httpInstance.post(urls.LEAVE_ENDPOINT + "/approve/all"),
        ),
      sudoRO: (formBody) =>
        httpHandler(() =>
          httpInstance.post(urls.LEAVE_ENDPOINT + "/sudoRO", formBody),
        ),
      staff: (id) =>
        httpHandler(() => httpInstance.get(urls.LEAVE_ENDPOINT + "/" + id)),
      summaryReport: () =>
        httpHandler(() =>
          httpInstance.get(urls.STAFF_ENDPOINT + "/leave-summary-report"),
        ),
    }),
    ot: buildEndpoints(urls.OT_ENDPOINT, {
      self: (params = {}) =>
        httpHandler(() => httpInstance.get(urls.OT_ENDPOINT + "/self", { params })),
      pending: () =>
        httpHandler(() => httpInstance.get(urls.OT_ENDPOINT + "/pending")),
      approve: (id, data) =>
        httpHandler(() =>
          httpInstance.put(urls.OT_ENDPOINT + `/${id}/approve`, data),
        ),
      approveAll: () =>
        httpHandler(() => httpInstance.post(urls.OT_ENDPOINT + "/approve/all")),
      staff: (id) =>
        httpHandler(() => httpInstance.get(urls.OT_ENDPOINT + "/" + id)),
    }),
    points: buildEndpoints(urls.ADD_POINTS_ENDPOINT, {
      add: (userId, formBody) =>
        httpHandler(() =>
          httpInstance.post(urls.ADD_POINTS_ENDPOINT + "/" + userId, formBody),
        ),
      update: (id, invoiceImg) =>
        httpInstance
          .put(urls.ADD_POINTS_ENDPOINT + "/" + id, {
            invoiceImg,
          })
          .then((response) => response.data)
          .catch(errorHandler),
    }),
    report: {
      ot: (params, isZip = false) =>
        httpHandler(() =>
          httpInstance.get(
            urls.STAFF_APPLICATION_ENDPOINT +
              "/report/OT?" +
              buildQueryParams(params),
            isZip && { responseType: "blob" },
          ),
        ),
      leave: (params, isZip = false) =>
        httpHandler(() =>
          httpInstance.get(
            urls.STAFF_APPLICATION_ENDPOINT +
              "/report/leave?" +
              buildQueryParams(params),
            isZip && { responseType: "blob" },
          ),
        ),
      transaction: (params) =>
        httpHandler(() =>
          httpInstance.get(
            urls.ORDER_ENDPOINT + "/report?" + buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      student: (id, params) =>
        httpHandler(() =>
          httpInstance.get(
            urls.STUDENT_ENDPOINT + `/${id}/report?` + buildQueryParams(params),
            {
              responseType: "arraybuffer",
            },
          ),
        ),
      addPointRecord: (params) =>
        httpHandler(() =>
          httpInstance.get(
            urls.ADD_POINTS_ENDPOINT + "/report?" + buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      teachingHoursSummaryReport: (params) =>
        httpHandler(() =>
          httpInstance.get(
            urls.STAFF_ENDPOINT +
              "/weekly-teaching-hours-report?download=true" +
              buildQueryParams(params),
            { responseType: "blob" },
          ),
        ),
      studentSummary: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STUDENT_ENDPOINT + `/summary?` + buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      studentSummaryV2: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STUDENT_ENDPOINT +
              `/class-summary?` +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      staffLeavesSummary: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STAFF_ENDPOINT +
              `/leave-summary-report?` +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      teacherSummary: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STAFF_ENDPOINT + `/summary-report?` + buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      userBalance: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.USER_ENDPOINT +
              `/user-balance-report?` +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      courseSummary: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.COURSE_ENDPOINT +
              `/summary-report?` +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      partTimeWageReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.LESSON_RECORD_ENDPOINT + "/report?" + buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      pettyCashSummaryReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STAFF_ENDPOINT +
              "/claim/petty-cash/report?" +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      lessonRecordSummaryReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.LESSON_RECORD_ENDPOINT +
              "/summary-report?" +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      dailyAttendanceReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.LESSON_RECORD_ENDPOINT +
              "/daily-report?" +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      individualStaffAttendanceReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.ATTENDANCE_ENDPOINT +
              `/individual/report?` +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      staffAttendanceReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.ATTENDANCE_ENDPOINT + "/report?" + buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      ludaAttendanceReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.ATTENDANCE_ENDPOINT +
              "/luda-attendance?" +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      geniusAttendanceReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.ATTENDANCE_ENDPOINT +
              "/genius-attendance?" +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      coursePurchaseRecordReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.COURSE_ENDPOINT +
              "/purchase-record/report?" +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      courseConfidenceStatistics: () =>
        httpHandler(() =>
          httpInstance(urls.STAFF_ENDPOINT + "/course-confidence/report", {
            responseType: "blob",
          }),
        ),
      staffClaims: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STAFF_ENDPOINT + "/claim/report?" + buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      partTimeTeacherStatus: () =>
        httpHandler(() =>
          httpInstance(urls.STAFF_ENDPOINT + "/pt-teacher-status", {
            responseType: "blob",
          }),
        ),
      staffLessonRecordReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STAFF_ENDPOINT +
              "/lesson-record-report?" +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
      studentCourseHoursReport: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STUDENT_ENDPOINT +
              `/student-course-hours-report?` +
              buildQueryParams(params),
            { responseType: "blob" },
          ),
        ),
      weeklyTeachingHours: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STAFF_ENDPOINT +
              `/weekly-teaching-hours-report?` +
              buildQueryParams(params),
            {
              responseType: "blob",
            },
          ),
        ),
    },
    selfV2: {
      applications: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.STAFF_APPLICATION_ENDPOINT +
              "/self/v2?" +
              buildQueryParams(params),
          ),
        ),
      attendances: (params) =>
        httpHandler(() =>
          httpInstance(
            urls.ATTENDANCE_ENDPOINT + "/self/v2?" + buildQueryParams(params),
          ),
        ),
    },
    lessonRecord: buildEndpoints(urls.LESSON_RECORD_ENDPOINT, {
      self: (params) =>
        httpHandler(() =>
          httpInstance.get(
            urls.LESSON_RECORD_ENDPOINT + "/self?" + buildQueryParams(params),
          ),
        ),
      legacy: buildEndpoints(urls.LESSON_RECORD_ENDPOINT + "/legacy"),
      topicFrequency: (params) =>
        httpHandler(() =>
          httpInstance.get(
            urls.LESSON_RECORD_ENDPOINT +
              "/topic_frequency?" +
              buildQueryParams(params),
          ),
        ),
      addReport: (id, formBody) =>
        httpHandler(() =>
          httpInstance.put(
            urls.LESSON_RECORD_ENDPOINT + "/" + id + "/add_report",
            formBody,
          ),
        ),
      addAttendance: (id, formBody) =>
        httpHandler(() =>
          httpInstance.put(
            urls.LESSON_RECORD_ENDPOINT + "/" + id + "/add_attendance",
            formBody,
          ),
        ),
      approve: (id, formBody) =>
        httpHandler(() =>
          httpInstance.put(
            urls.LESSON_RECORD_ENDPOINT + "/" + id + "/approve",
            formBody,
          ),
        ),
      studentRecord: buildEndpoints(
        urls.LESSON_RECORD_ENDPOINT + "/student_record",
        {
          self: (id, queryParams, filters) =>
            httpHandler(() => {
              return httpInstance.get(
                `${urls.LESSON_RECORD_ENDPOINT + "/student_record/self"}?${buildQueryParams(queryParams)}&${
                  filters !== undefined ? buildQueryParams(filters) : ""
                }`,
              );
            }),
          addHomework: (studentLessonRecordId, body) => {
            httpHandler(() => {
              return httpInstance.post(
                `${urls.LESSON_RECORD_ENDPOINT + `/student_lesson_record/${studentLessonRecordId}/homework`}`,
                body,
              );
            });
          },
          deleteHomework: (homeworkId) => {
            httpHandler(() => {
              return httpInstance.delete(
                `${urls.LESSON_RECORD_ENDPOINT}/student_lesson_record/${homeworkId}/homework`,
              );
            });
          },
          updateAssets: (body) =>
            httpHandler(() => {
              return httpInstance.put(
                `${urls.LESSON_RECORD_ENDPOINT}/add_assets`,
                body,
              );
            }),
          deleteAsset: (id) =>
            httpHandler(() =>
              httpInstance.delete(
                `${urls.LESSON_RECORD_ENDPOINT}/student_record/asset/${id}`,
              ),
            ),
        },
      ),
      editLesson: (id, lessonId) =>
        httpHandler(() =>
          httpInstance.put(urls.LESSON_RECORD_ENDPOINT + "/" + id, {
            lessonId,
          }),
        ),
    }),
    parent: buildEndpoints(urls.USER_ENDPOINT),
    role: buildEndpoints(urls.ROLE_ENDPOINT),
    staff: buildEndpoints(urls.STAFF_ENDPOINT, {
      self: () =>
        httpHandler(() => httpInstance.get(urls.STAFF_ENDPOINT + "/self")),
      courseConfidence: buildEndpoints(
        urls.STAFF_ENDPOINT + "/course-confidence",
      ),
      ranking: (params) =>
        httpHandler(() =>
          httpInstance.get(
            urls.STAFF_ENDPOINT + "/ranking?" + buildQueryParams(params),
          ),
        ),
      availability: buildEndpoints(urls.STAFF_AVAILABILITY_ENDPOINT, {
        self: (params) =>
          httpHandler(() =>
            httpInstance.get(
              urls.STAFF_AVAILABILITY_ENDPOINT +
                "/self?" +
                buildQueryParams(params),
            ),
          ),
      }),
      min: (id) =>
        httpHandler(() => httpInstance.get(`${urls.STAFF_ENDPOINT}/${id}/min`)),
      claims: buildEndpoints(urls.STAFF_ENDPOINT + "/claim", {
        self: (params) =>
          httpHandler(() =>
            httpInstance.get(
              urls.STAFF_ENDPOINT + "/claim/self?" + buildQueryParams(params),
            ),
          ),
        approve: (claimId, body) =>
          httpHandler(() =>
            httpInstance.put(
              urls.STAFF_ENDPOINT + `/claim/${claimId}/approve`,
              body,
            ),
          ),
        payout: (claimId) =>
          httpHandler(() =>
            httpInstance.post(urls.STAFF_ENDPOINT + `/claim/${claimId}/payout`),
          ),
      }),
    }),
    course: buildEndpoints(urls.COURSE_ENDPOINT, {
      purchaseRecord: buildEndpoints(
        urls.COURSE_ENDPOINT + "/purchase-record",
        {
          addPayment: (id, formData) =>
            httpInstance.post(
              urls.COURSE_ENDPOINT + "/purchase-record/" + id + "/add-payment",
              formData,
            ),
        },
      ),
    }),
    attendance: buildEndpoints(urls.ATTENDANCE_ENDPOINT, {
      self: () =>
        httpHandler(() => httpInstance.get(urls.ATTENDANCE_ENDPOINT + "/self")),
      application: buildEndpoints(urls.ATTENDANCE_ENDPOINT + "/application", {
        approve: (id) =>
          httpHandler(() =>
            httpInstance.post(
              urls.ATTENDANCE_ENDPOINT + `/application/${id}/approve`,
            ),
          ),
        self: (queryParams) =>
          httpHandler(() =>
            httpInstance.get(
              urls.ATTENDANCE_ENDPOINT +
                "/application/self?" +
                buildQueryParams(queryParams),
            ),
          ),
      }),
    }),
    studentPmr: {
      get: (studentId) =>
        httpHandler(() =>
          httpInstance.get(
            `${urls.STUDENT_ENDPOINT}/${studentId}/parent_meeting`,
          ),
        ),
      create: (studentId, formData) =>
        httpHandler(() =>
          httpInstance.post(
            `${urls.STUDENT_ENDPOINT}/${studentId}/parent_meeting`,
            formData,
          ),
        ),
      update: (studentId, meetingId, formData) =>
        httpHandler(() =>
          httpInstance.put(
            `${urls.STUDENT_ENDPOINT}/${studentId}/parent_meeting/${meetingId}`,
            formData,
          ),
        ),
      delete: (studentId, meetingId) =>
        httpHandler(() =>
          httpInstance.delete(
            `${urls.STUDENT_ENDPOINT}/${studentId}/parent_meeting/${meetingId}`,
          ),
        ),
    },
    getMinStudentCourse: (studentId) =>
      httpHandler(() =>
        httpInstance.get(`${urls.STUDENT_ENDPOINT}/${studentId}/course/min`),
      ),
    student: buildEndpoints(urls.STUDENT_ENDPOINT, {
      traits: buildEndpoints(urls.STUDENT_ENDPOINT + "/traits"),
      birthdays: () =>
        httpHandler(() =>
          httpInstance.get(urls.STUDENT_ENDPOINT + "/birthdays"),
        ),
      centres: () =>
        httpHandler(() => httpInstance.get(urls.STUDENT_ENDPOINT + "/centres")),
      editStudentCourse: (courseId, formData) =>
        httpHandler(() =>
          httpInstance.patch(
            `${urls.STUDENT_ENDPOINT}/course/${courseId}/edit-hours`,
            formData,
          ),
        ),
      getAbsoluteMin: (id, queryParams) =>
        httpHandler(() =>
          httpInstance.get(
            urls.STUDENT_ENDPOINT + "/min?" + buildQueryParams(queryParams),
          ),
        ),
      getOneAbsoluteMin: (id, queryParams) =>
        httpHandler(() =>
          httpInstance.get(
            `${urls.STUDENT_ENDPOINT}/${id}/min?${buildQueryParams(queryParams)}`,
          ),
        ),
      getLeanV2: (id, queryParams) =>
        httpHandler(() =>
          httpInstance.get(
            `${urls.STUDENT_ENDPOINT}/${id}/lean?${buildQueryParams(queryParams)}`,
          ),
        ),
      getLean: (id, queryParams) =>
        httpHandler(() =>
          httpInstance.get(urls.STUDENT_ENDPOINT + "/lean/" + id),
        ),
      lowHoursCourse: buildEndpoints(urls.COURSE_ENDPOINT + "/low-hours"),
      lessonRecords: (studentId, params) =>
        httpHandler(() =>
          httpInstance.get(
            `${urls.STUDENT_ENDPOINT}/${studentId}/lesson_record?${buildQueryParams(params)}`,
          ),
        ),
      getCourses: (studentId) =>
        httpHandler(() =>
          httpInstance.get(`${urls.STUDENT_ENDPOINT}/${studentId}/course`),
        ),
      getCourseLedgerRecords: (studentCourseId) =>
        httpHandler(() =>
          httpInstance.get(
            `${urls.STUDENT_ENDPOINT}/course/${studentCourseId}`,
          ),
        ),
    }),
  };
})();

export default apiService;
